import Vue from 'vue';
import Router, { Route } from 'vue-router';
import Vuex, { Commit } from 'vuex';

import { getJson } from 'frontend/utils/ajax';
import { getServerSideContext } from 'frontend/utils/ssr-context';
import { snakeToCamel } from 'frontend/utils/textFormatters';

import banner from 'frontend/store/modules/banner';
import profitwellApiToken from 'frontend/store/modules/analytics/profitwell-api-token';
import openPositions from 'frontend/store/modules/company/open-positions';
import daysLeftInTrial from 'frontend/store/modules/premium/days-left-in-trial';
import canExtendTrial from 'frontend/store/modules/premium/can-extend-trial';
import subscription from 'frontend/store/modules/premium/subscription';
import subscriptionEndDate from 'frontend/store/modules/premium/subscription-end-date';
import recentLessonInfo from 'frontend/store/modules/userhome/recent-lesson-info';
import referralSummary from 'frontend/store/modules/referral/referral-summary';
import referralDetails from 'frontend/store/modules/referral/referral-details';
import referralModalInfo from 'frontend/store/modules/referral/referral-modal-info';
import viewedReferralModal from 'frontend/store/modules/referral/viewed-referral-modal';
import referrerAnnualPlanCostPerMonth from 'frontend/store/modules/referral/referrer-annual-plan-cost-per-month';
import recommendedLessonsInfo from 'frontend/store/modules/userhome/recommeded-lessons-info';
import recommendedLearningPathInfo from 'frontend/store/modules/userhome/recommended-learning-paths';
import streakPopup from 'frontend/store/modules/streak-popup';
import leagueLeaderboard from 'frontend/store/modules/leagueLeaderboard';
import leagueLessonXP from 'frontend/store/modules/leagueLessonXP';
import leaguesMeta from 'frontend/store/modules/leaguesMeta';
import { savvyPage, savvyLoggedOutHomepage } from 'frontend/store/modules/savvy';
import summerPass from 'frontend/store/modules/summer-pass';
import categorizedCourses from './modules/courses/categorized-courses';
import categorizedCoursesByDifficulty from './modules/courses/categorized-courses-by-difficulty';
import challengeEmailsModal from './modules/courses/challenge-emails-modal';
import chapters from './modules/courses/chapters';
import course from './modules/courses/course';
import featuredCourses from './modules/courses/featured-courses';
import justLaunchedCourses from './modules/courses/just-launched-courses';
import popularCourses from './modules/courses/popular-courses';
import recentCourses from './modules/courses/recent-courses';
import secondaryFeaturedCourses from './modules/courses/secondary-featured-courses';
import userCourses from './modules/courses/user-courses';
import classrooms from './modules/classrooms/classrooms';
import joinClassroomFromCode from './modules/classrooms/join-classroom-from-code';
import educatorApplication from './modules/classrooms/educator-application';
import numCourses from './modules/courses/num-courses';
import learningPath from './modules/courses/learning-path-single';
import generalPath from './modules/courses/general-path';
import learningGroups from './modules/courses/learning-groups';
import recommendedCourseKeywords from './modules/courses/recommended-course-keywords';
import faqs from './modules/faqs';
import userHome from './modules/user-home';
import exportData from './modules/export-data';
import downloadUserContentBundle from './modules/downloadUserContentBundle';
import loggedOutCoupon from './modules/logged-out-coupon';
import notificationsDrawer from './modules/notifications-drawer';
import pageHeader from './modules/page-header';
import response from './modules/response';
import savedsets from './modules/savedsets';
import userCategories from './modules/user-categories';
import userCategory from './modules/user-category';
import sponsorModalInfo from './modules/sponsor-modal-info';
import sponsorPbsNova from './modules/sponsor-pbs-nova';
import { appDownloadLinks, appInstallationStatus } from './modules/app-download-links';
import accountDeactivatedMessage from './modules/account-deactivated-message';

import courseAdminLink from './modules/authors/course-admin-link';
import courseEdit from './modules/authors/course-edit';
import courseVersions from './modules/authors/course-versions';
import courseLessonSearch from './modules/authors/course-lesson-search';
import learningPathV2 from './modules/learning-path-v2';

import premiumPurchaseInfo from './modules/premium/premiumPurchaseInfo';
import paywall, { prepurchaseDestinationUrl } from './modules/premium/paywall';
import checkoutSession from './modules/premium/checkoutSession';

import groupPurchasePricing from './modules/premium/groupPurchasePricing';
import groupPurchaseInfo from './modules/premium/groupPurchaseInfo';
import planFilters from './modules/premium/planFilters';
import premiumGroup from './modules/premium/premiumGroup';
import premiumGroupSeatRedeem from './modules/premium/premiumGroupSeatRedeem';
import premiumTestimonials from './modules/premium/premiumTestimonials';
import premiumCancellation from './modules/premium/premiumCancellation';

import checkoutSuccessContext from './modules/premium/checkoutSuccessContext';
import giftPurchaseCheckingStatus from './modules/premium/gift-purchase-checking-status';
import giftPurchaseSuccess from './modules/premium/giftPurchaseSuccess';
import giftPlans from './modules/premium/gift-plans';
import giftPurchaseAccept from './modules/premium/giftPurchaseAccept';
import paymentsGiftsSuccess from './modules/payments/giftsSuccess';

import globals from './modules/globals';
import signupModal from './modules/signup-modal';
import wadtudrActions from './modules/wadtudr-actions';
import errorModal from './modules/error-modal';
import result from './modules/result';

import testimonials from './modules/company/testimonials';
import careers from './modules/company/careers';
import landingLearningPaths from './modules/landing-learning-paths';
import numUsers from './modules/num-users';

import marketingLandingPage from './modules/landing/marketingLandingPage';

import lesson from './modules/lessons/lesson';
import lessonEditor from './modules/lessons/lessonEditor';
import lessonLastAutosave from './modules/lessons/lessonLastAutosave';
import lessonVersions from './modules/lessons/lessonVersions';
import lessonAdminLink from './modules/lessons/lessonAdminLink';
import userState from './modules/lessons/userState';
import transpilerSha from './modules/lessons/transpilerSha';
import packages from './modules/lessons/packages';
import lessonOwner from './modules/lessons/lessonOwner';

import staffMemberResponsible from './modules/staffMemberResponsible';
import staffMetaData from './modules/profile/staffMetaData';
import profile from './modules/profile/profile';
import segmentationQuestion from './modules/segmentation/segmentation-question';
import userSegmentations from './modules/segmentation/user-segmentation-bundle';
import learningPathSuggestions from './modules/nux/learning-path-suggestions';

import isFirstCourse from './modules/nux/is-first-course';
import firstCourseChaptersCount from './modules/nux/course-chapters-count';

import authorLessons from './modules/authorLessons';

import experiments from './modules/admin/experiments';

import continueLearning from './modules/userhome/continue-learning-lessons';

import showShoppingVersion from './modules/courses/show-shopping-version';
import relatedLearningPathInfo from './modules/related-learning-path-info';
import xpPerSolvableConfig from './modules/xp-per-solvable-config';
import nuxRecommendations from './modules/nux-recommendations';
import nuxSignupPage from './modules/nux-signup';
import nextLearningPathCourse from './modules/next-learning-path-course';
import cancellationFlowDiscount from './modules/premium/cancellationFlowDiscount';
import nextSubscriptionPayment from './modules/premium/nextSubscriptionPayment';
import ruxWelcomeBackOrganic from './modules/rux-welcome-back-organic';

// Use this to prevent making a request twice. This can happen in CSR if there's a redirect in the
// ajax request
let handlingResponseForPath: string | null = null;

function handleResponse(commit: Commit, router: Router, resp: { [key: string]: any }) {
  // Handling this in this function so reset it here
  if (handlingResponseForPath) {
    handlingResponseForPath = null;
  }

  if (resp) {
    const { response: respResponse, results, globals: respGlobals, $redirected } = resp;

    if ($redirected) {
      handlingResponseForPath = $redirected.to;
      // If the request was redirect, push to the correct route before committing the new data
      router.push($redirected.to, () => {
        delete resp.$redirected;
        handleResponse(commit, router, resp);
      });
      return;
    }

    if (respResponse) {
      commit('response/setValue', respResponse);
    }

    if (results) {
      Object.entries(results).forEach(([key, value]: [string, any]) => {
        commit(`${snakeToCamel(key)}/setValue`, value);
      });
    }

    if (respGlobals) {
      commit('globals/setValue', respGlobals);
    }
  }
}

export default function createStore(router: Router) {
  Vue.use(Vuex);
  return new Vuex.Store({
    actions: {
      async fetchResults({ commit, dispatch }, route: Route) {
        // Already requested this data via redirect, just updating the router to match
        if (handlingResponseForPath && route.fullPath === handlingResponseForPath) {
          return;
        }
        if (typeof window !== 'undefined') {
          try {
            handleResponse(commit, router, await getJson(route.fullPath));
          } catch (e) {
            dispatch('errorModal/showMessage');
          }
          return;
        }
        const context: { backend: any } = getServerSideContext();
        handleResponse(commit, router, context.backend);
      },
    },
    modules: {
      banner,
      categorizedCourses,
      categorizedCoursesByDifficulty,
      challengeEmailsModal,
      chapters,
      course,
      exportData,
      downloadUserContentBundle,
      featuredCourses,
      globals,
      justLaunchedCourses,
      loggedOutCoupon,
      notificationsDrawer,
      pageHeader,
      popularCourses,
      recentCourses,
      response,
      savedsets,
      secondaryFeaturedCourses,
      signupModal,
      wadtudrActions,
      userCategories,
      userCategory,
      segmentationQuestion,
      userSegmentations,
      sponsorModalInfo,
      sponsorPbsNova,
      appDownloadLinks,
      appInstallationStatus,
      accountDeactivatedMessage,
      courseAdminLink,
      courseEdit,
      courseVersions,
      courseLessonSearch,
      errorModal,
      userCourses,
      classrooms,
      joinClassroomFromCode,
      educatorApplication,
      numCourses,
      learningPath,
      learningPathSuggestions,
      learningPathV2,
      nuxRecommendations,
      nuxSignupPage,
      nextLearningPathCourse,
      firstCourseChaptersCount,
      generalPath,
      learningGroups,
      premiumPurchaseInfo,
      paywall,
      prepurchaseDestinationUrl,
      checkoutSession,
      groupPurchasePricing,
      groupPurchaseInfo,
      premiumGroup,
      premiumGroupSeatRedeem,
      planFilters,
      premiumTestimonials,
      checkoutSuccessContext,
      giftPurchaseCheckingStatus,
      giftPurchaseSuccess,
      paymentsGiftsSuccess,
      giftPlans,
      giftPurchaseAccept,
      premiumCancellation,
      result,
      testimonials,
      careers,
      landingLearningPaths,
      numUsers,
      recommendedCourseKeywords,
      lesson,
      lessonEditor,
      lessonLastAutosave,
      lessonVersions,
      lessonAdminLink,
      userState,
      transpilerSha,
      packages,
      lessonOwner,
      staffMemberResponsible,
      staffMetaData,
      profile,
      isFirstCourse,
      authorLessons,
      experiments,
      profitwellApiToken,
      faqs,
      userHome,
      openPositions,
      daysLeftInTrial,
      canExtendTrial,
      subscription,
      subscriptionEndDate,
      recentLessonInfo,
      referralSummary,
      referralDetails,
      referralModalInfo,
      referrerAnnualPlanCostPerMonth,
      relatedLearningPathInfo,
      viewedReferralModal,
      continueLearning,
      recommendedLessonsInfo,
      recommendedLearningPathInfo,
      marketingLandingPage,
      streakPopup,
      leagueLeaderboard,
      leagueLessonXP,
      leaguesMeta,
      savvyPage,
      savvyLoggedOutHomepage,
      summerPass,
      showShoppingVersion,
      xpPerSolvableConfig,
      cancellationFlowDiscount,
      nextSubscriptionPayment,
      ruxWelcomeBackOrganic,
    },
  });
}
